// silent SSO(prompt=none) 시도 상태를 sso-auth → officenext → callback 왕복 동안 전달한다.
// "silent 시도였는지"(플래그)와 "원래 가려던 딥링크 경로"는 항상 함께 쓰고 함께 지운다 —
// 플래그만 남으면 callback 이 경로를 잃고 path 없는 /auth/login 으로 폴백해, 수동 로그인 후
// 원래 화면으로 복귀하지 못한다(딥링크 path 보존 회귀).
//
// 의존성 zero 로 유지할 것: sso-auth(웹)와 callback 양쪽에서 import 하는 저수준 헬퍼라
// redux/api/i18n 을 끌어들이면 jest.mock 호이스팅 사고가 난다(extensionBridge 전례).
const SILENT_FLAG_KEY = 'silentAuth'
const SILENT_PATH_KEY = 'silentAuthPath'

// 내부 경로만 허용 — protocol-relative(//host) 오픈 리다이렉트 방지.
// 경로 부분(첫 `?`·`#` 앞)에 `\` 와 탭·개행이 있으면 거부한다. 브라우저 URL 파서는 `\` 를 `/` 로 보고
// 탭·개행을 지우는데, 라우터는 URL 파서에 넘기기 전에 `.` 구간을 먼저 정리한다. 그래서 정규화한 문자열만
// 검사하면 라우터 해석 뒤에 `//` 로 시작하는 값이 남는다. 쿼리·해시 안의 문자는 이 문제와 무관해 막지 않는다.
// (`utils/__tests__/silentAuth.test.js` 의 '통과시킨 값은 실제 라우터에서 예외 없이 이동한다' 가 단언)
export function getInternalPath(rawPath) {
  if (typeof rawPath !== 'string') {
    return null
  }
  const pathPart = rawPath.split(/[?#]/, 1)[0]
  if (/[\\\t\n\r]/.test(pathPart)) {
    return null
  }
  return pathPart.startsWith('/') && !pathPart.startsWith('//') ? rawPath : null
}

// 반환값 false = sessionStorage 사용 불가(사생활 보호 모드/서드파티 저장소 차단).
// 이때 호출자는 silent 시도를 포기해야 한다 — 플래그 없이 officenext 로 보내면 "세션 없음"
// 실패가 조용히 폴백되지 않고 사용자에게 에러 팝업으로 노출된다.
export function markSilentAuth(returnPath) {
  try {
    sessionStorage.setItem(SILENT_FLAG_KEY, 'true')

    const internalPath = getInternalPath(returnPath)
    if (internalPath != null) {
      sessionStorage.setItem(SILENT_PATH_KEY, internalPath)
    } else {
      // 이전 시도의 경로가 남아 엉뚱한 화면으로 복귀하지 않도록 정리.
      sessionStorage.removeItem(SILENT_PATH_KEY)
    }

    return true
  } catch {
    return false
  }
}

// 1회 소비(읽고 지움). 읽기 실패는 silent 로 간주하지 않는다(isSilent=false) — 에러를 조용히
// 삼키는 대신 호출자의 안내 팝업 분기로 통과시킨다.
export function consumeSilentAuth() {
  try {
    const isSilent = sessionStorage.getItem(SILENT_FLAG_KEY) != null
    const path = getInternalPath(sessionStorage.getItem(SILENT_PATH_KEY))

    sessionStorage.removeItem(SILENT_FLAG_KEY)
    sessionStorage.removeItem(SILENT_PATH_KEY)

    return { isSilent, path }
  } catch {
    return { isSilent: false, path: null }
  }
}

// silent 시도가 성공해 로그인까지 갔을 때의 잔여 상태 정리. 남겨두면 같은 탭의 이후 수동
// 로그인 실패가 silent 로 오인돼 안내 팝업이 삼켜지고, 지난 딥링크 경로로 복귀한다.
export function clearSilentAuth() {
  try {
    sessionStorage.removeItem(SILENT_FLAG_KEY)
    sessionStorage.removeItem(SILENT_PATH_KEY)
  } catch {
    // 정리 실패가 로그인 흐름을 막아선 안 된다.
  }
}
